import { writable } from "svelte/store";
import {
  SendCreateCustomerDoc,
  SendCreateCustomerAccessTokenDoc,
  SendDeleteCustomerAccessTokenDoc,
  SendRenewCustomerAccessTokenDoc,
  SendDeleteCustomerAddressDoc,
  GetCustomerIdDoc,
  GetCustomerTagsDoc,
} from "$lib/gql/gen/codegen";

import type {
  CustomerCreateInput,
  CustomerAccessTokenCreateInput,
  GetCustomerIdQuery,
  GetCustomerTagsQuery,
} from "$lib/gql/gen/codegen";
import type {
  SendCreateCustomerMutation,
  SendCreateCustomerAccessTokenMutation,
  SendDeleteCustomerAccessTokenMutation,
  SendRenewCustomerAccessTokenMutation,
  SendDeleteCustomerAddressMutation,
} from "$lib/gql/gen/codegen";

import { subscribeToNewsletter } from "$lib/utils/subscribeToNewsletter";

import type { SetMarketingMutation } from "$lib/gql/genCms/codegen";
import { SetMarketingDoc } from "$lib/gql/genCms/codegen";
import client from "$lib/gql/apolloClient";
import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { get } from "svelte/store";
import { goto } from "$app/navigation";
import cfg from "$lib/config/config";
import { Errors } from "$lib/data/errors.data";
import { voyadoIdentifyCustomer } from "$lib/utils/voyado";
import { linkTo } from "$lib/utils/url";
import { browser } from "$app/environment";
import { cart } from "./cart";

export type MarketingInput = {
  gender?: string;
  locale?: string;
};

export type Account = {
  accessToken: string;
  expiresAt: string;
  userId: string;
  email: string;
  firstName?: string;
  masterCustomer?: boolean;
};
export type customerCreate = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};
export function accountStore(
  client: ApolloClient<NormalizedCacheObject>,
  namespace: string,
  localStorage?: Storage,
) {
  let initialState: Account | null = null;
  if (localStorage) {
    const accountString = localStorage.getItem(`${namespace}:account`);
    try {
      initialState = accountString !== null ? JSON.parse(accountString) : null;
    } catch {
      initialState = null;
    }
  }
  const account = writable(initialState);
  if (localStorage) {
    account.subscribe(accessToken => {
      localStorage.setItem(`${namespace}:account`, JSON.stringify(accessToken));
    });
  }
  return {
    subscribe: account.subscribe,
    set: account.set,
    register: async (
      input: CustomerCreateInput,
      marketingInput: MarketingInput,
    ) => {
      try {
        const res = await client.mutate<SendCreateCustomerMutation>({
          mutation: SendCreateCustomerDoc,
          variables: {
            input,
          },
        });
        const data = res.data?.customerCreate;
        if ((data?.customerUserErrors.length ?? 0) > 0) {
          //Handle when customer exists but is not registered
          if (data?.customerUserErrors[0].code === "CUSTOMER_DISABLED") {
            const existingCustomerResponse = await fetch(
              `${cfg.BASE_PATH || ""}/api/login`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: input.email,
                }),
              },
            );

            const existingCustomer = await existingCustomerResponse.json();

            const userId = existingCustomer.id;

            const genderPrefUpdate = await fetch(
              `${cfg.BASE_PATH || ""}/api/gender-preferences`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  customerId: userId,
                  gender: marketingInput.gender,
                }),
              },
            );

            const genderPrefUpdateData = await genderPrefUpdate.json();

            const detailsUpdate = await fetch(
              `${cfg.BASE_PATH || ""}/api/update-customer`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  customerId: userId,
                  firstName: input.firstName,
                  lastName: input.lastName,
                }),
              },
            );

            const detailsUpdateData = await detailsUpdate.json();

            if (
              !detailsUpdateData.customerUpdated ||
              genderPrefUpdateData.error
            ) {
              return [{ code: "ERROR" }];
            }

            if (input.acceptsMarketing) {
              subscribeToNewsletter(
                input.email,
                marketingInput.gender,
                null,
                marketingInput.locale,
              );
            }
          }

          // Return errors to display on page
          return data?.customerUserErrors;
        } else if (data?.customer?.id) {
          const userId = data.customer.id;
          try {
            const res =
              await client.mutate<SendCreateCustomerAccessTokenMutation>({
                mutation: SendCreateCustomerAccessTokenDoc,
                variables: {
                  input: {
                    email: input.email,
                    password: input.password,
                  },
                },
              });
            const data = res.data?.customerAccessTokenCreate;
            if (
              !data?.customerAccessToken &&
              (data?.customerUserErrors.length ?? 0) > 0
            ) {
              goto(linkTo("login"));
            } else if (data.customerAccessToken) {
              const customerDetails = await client.query<GetCustomerTagsQuery>({
                query: GetCustomerTagsDoc,
                variables: {
                  accessToken: data.customerAccessToken.accessToken,
                },
              });
              const inLoyaltyProgram =
                customerDetails.data.customer?.tags?.includes(
                  "Master Customer",
                );

              // Add to store and redirect to account overview page
              account.set({
                ...data.customerAccessToken,
                masterCustomer: inLoyaltyProgram,
                email: input.email,
                userId: userId,
              });
              if (get(cart)?.id) {
                cart.buyerIdentity(get(cart).id, {
                  customerAccessToken: data.customerAccessToken.accessToken,
                  email: input.email,
                });
              }

              // Set marketing prefs
              await client.mutate<SetMarketingMutation>({
                mutation: SetMarketingDoc,
                variables: {
                  token: data.customerAccessToken.accessToken,
                  input: {
                    acceptsMarketing: input.acceptsMarketing,
                    gender: marketingInput.gender,
                    locale: marketingInput.locale,
                  },
                },
                context: { clientName: "cms" },
              });

              // Set voyado gender
              await fetch(`${cfg.BASE_PATH || ""}/api/gender-preferences`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  customerId: userId,
                  gender: marketingInput.gender,
                }),
              });

              goto(linkTo("account"));
            }
          } catch (error) {
            console.error(error);
            return [{ code: "ERROR" }];
          }
        }

        voyadoIdentifyCustomer(input.email);
      } catch (error) {
        console.error(error);
        return [{ code: "ERROR" }];
      }
    },
    create: async (
      input: CustomerAccessTokenCreateInput,
      redirectToCheckout?: boolean,
      redirectToLoyalty?: boolean,
    ) => {
      try {
        const res = await client.mutate<SendCreateCustomerAccessTokenMutation>({
          mutation: SendCreateCustomerAccessTokenDoc,
          variables: {
            input,
          },
        });
        const data = res.data?.customerAccessTokenCreate;
        if (
          !data?.customerAccessToken &&
          (data?.customerUserErrors.length ?? 0) > 0
        ) {
          // Return errors to display on page
          const existingCustomerResponse = await fetch(
            `${cfg.BASE_PATH || ""}/api/login`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                email: input.email,
              }),
            },
          );

          const existingCustomer = await existingCustomerResponse.json();

          if (existingCustomer.customerExists === false) {
            return [
              {
                code: "USER_NOT_REGISTERED",
                field: null,
                message: "User not registered",
                __typename: "CustomerUserError",
              },
            ];
          }

          if (
            existingCustomer.data &&
            existingCustomer.data.customers.edges.length > 0
          ) {
            console.log(data?.customerUserErrors);
          }
          return data?.customerUserErrors;
        } else if (data?.customerAccessToken) {
          try {
            const res = await client.query<GetCustomerIdQuery>({
              query: GetCustomerIdDoc,
              variables: {
                accessToken: data.customerAccessToken.accessToken,
              },
            });
            const customerId = res.data.customer?.id;

            const customerDetails = await client.query<GetCustomerTagsQuery>({
              query: GetCustomerTagsDoc,
              variables: {
                accessToken: data.customerAccessToken.accessToken,
              },
            });
            const inLoyaltyProgram =
              customerDetails.data.customer?.tags?.includes("Master Customer");

            // Add to store and redirect to account overview page
            if (customerId) {
              account.set({
                ...data.customerAccessToken,
                masterCustomer: inLoyaltyProgram,
                email: input.email,
                userId: customerId,
              });
              if (get(cart)?.id) {
                cart.buyerIdentity(get(cart).id, {
                  customerAccessToken: data.customerAccessToken.accessToken,
                  email: input.email,
                });
              }
            }
            if (redirectToCheckout) {
              goto(linkTo("bag?checkout"));
            } else if (redirectToLoyalty) {
              goto(linkTo("account/circuit-master"));
            } else {
              goto(linkTo("account"));
            }
          } catch (error) {
            console.error(error);
            goto(linkTo("500"));
          }
        }
        voyadoIdentifyCustomer(input.email);
      } catch (error) {
        console.error("error", error);
        if (
          error instanceof Error &&
          error.message === Errors.LoginAttemptExceeded
        ) {
          return [
            {
              code: "LOGIN_ATTEMPT_EXCEEDED",
              message: Errors.LoginAttemptExceeded,
            },
          ];
        } else {
          goto(linkTo("500"));
        }
      }
    },
    logout: async () => {
      const token = get(account);
      if (token) {
        try {
          await client.mutate<SendDeleteCustomerAccessTokenMutation>({
            mutation: SendDeleteCustomerAccessTokenDoc,
            variables: {
              token: token.accessToken,
            },
          });
        } catch (error) {
          console.error(error);
          goto(linkTo("500"));
        }
        account.set(null);
        // Redirect to the shopify stores logout page, this will then redirect
        // us back to our home page
        goto(`https://${namespace}.myshopify.com/account/logout`);
      }
    },
    checkForAccountRedirect: () => {
      const token = get(account);
      if (token) {
        goto(linkTo("account"));
      }
    },
    checkForLoyaltyRedirect: async () => {
      const token = get(account);

      if (token) {
        try {
          const customerDetails = await client.query<GetCustomerTagsQuery>({
            query: GetCustomerTagsDoc,
            variables: {
              accessToken: token.accessToken,
            },
          });
          const inLoyaltyProgram =
            customerDetails.data.customer?.tags?.includes("Master Customer");

          if (inLoyaltyProgram) {
            account.set({
              ...token,
              masterCustomer: true,
              firstName: customerDetails.data.customer?.firstName,
            });
            return true;
          } else {
            goto(linkTo("account"));
          }
        } catch (error) {
          console.error(error);
          goto(linkTo("500"));
        }
      } else {
        goto(linkTo("account/circuit-master/login"));
      }
    },
    isMasterCustomer: async () => {
      const token = get(account);

      if (token) {
        try {
          const customerDetails = await client.query<GetCustomerTagsQuery>({
            query: GetCustomerTagsDoc,
            variables: {
              accessToken: token.accessToken,
            },
          });

          const inLoyaltyProgram =
            customerDetails.data.customer?.tags?.includes("Master Customer");

          account.set({
            ...token,
            masterCustomer: inLoyaltyProgram,
            firstName: customerDetails.data.customer?.firstName,
          });
          return inLoyaltyProgram;
        } catch (error) {
          console.error(error);
          return false;
        }
      } else {
        return false;
      }
    },
    renewToken: async () => {
      const currentAccount = get(account);
      if (currentAccount) {
        try {
          const res = await client.mutate<SendRenewCustomerAccessTokenMutation>(
            {
              mutation: SendRenewCustomerAccessTokenDoc,
              variables: {
                token: currentAccount.accessToken,
              },
            },
          );
          // If there are errors or the attempt fails, remove token from store and redirect to homepage
          const data = res.data?.customerAccessTokenRenew;
          if ((data?.userErrors.length ?? 0) > 0) {
            account.set(null);
            goto(linkTo("account/login"), { replaceState: true });
          } else if (data?.customerAccessToken) {
            account.set({
              ...currentAccount,
              accessToken: data.customerAccessToken.accessToken,
              expiresAt: data.customerAccessToken.expiresAt,
            });
          }
        } catch (error) {
          console.error(error);
          account.set(null);
          goto(linkTo("account/login"), { replaceState: true });
        }
      } else {
        goto(linkTo("account/login"), { replaceState: true });
      }
    },
    deleteAddress: async (id: string) => {
      const token = get(account);

      if (token) {
        try {
          const res = await client.mutate<SendDeleteCustomerAddressMutation>({
            mutation: SendDeleteCustomerAddressDoc,
            variables: {
              id: id,
              token: token.accessToken,
            },
          });
          const data = res.data?.customerAddressDelete;
          if ((data?.customerUserErrors.length ?? 0) > 0) {
            goto(linkTo("account/address/list"));
          } else {
            return data?.deletedCustomerAddressId;
          }
        } catch (error) {
          console.error(error);
          goto(linkTo("account/address/list"));
        }
        goto(linkTo("account/address/list"));
      }
    },
  };
}
export const account = accountStore(
  client,
  cfg.SHOPIFY_STORE,
  browser && navigator.cookieEnabled ? localStorage : undefined,
);
